<template>
  <div
    :class="[$style.container, computedClasses]"
    @click="handleFocus"
  >
    <div :class="$style.inner">
      <div :class="$style.wrapper">
        <div :class="$style.header">
          <label
            :for="internalId"
            :class="[$style.label, computedClasses]"
            v-text="label"
          />

          <div
            v-if="useCounter"
            v-color="internalFocus ? 'black-2' : 'white'"
            v-opacity=".5"
            v-text="counter"
          ></div>

          <base-icon
            v-if="disabled"
            v-d-flex
            v-color:grey
            name="security_lock-circle"
          />

          <base-icon
            v-else-if="error"
            v-d-flex
            v-color:red
            name="notifications_alert-circle"
          />

          <help-link
            v-else-if="helpLink"
            v-color="internalFocus ? 'violet' : 'white'"
            :source="helpLink"
          />

          <div
            v-if="$scopedSlots.hasOwnProperty('icon')"
            v-d-flex
            @click.stop
          >
            <slot
              name="icon"
              :focus="internalFocus"
            />
          </div>
        </div>

        <div :class="$style.content">
          <component
            :is="currentComponent"
            :id="internalId"
            :ref="internalRef"
            :type="type"
            :value="value"
            :inputmode="inputmode"
            :placeholder="placeholder"
            :disabled="disabled"
            :maxlength="maxlength"
            :readonly="readonly"
            :force-replace="forceReplace"
            :force-format="forceFormat"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"
            @submit="handleSubmit"
            @keydown="handleKeydown"
          />

          <div
            v-if="$slots.hasOwnProperty('target-link')"
            :class="$style.link"
          >
            <slot name="target-link" />
          </div>
        </div>
      </div>

      <div
        v-if="$slots.hasOwnProperty('attachment')"
        :class="$style.attachment"
        @click.stop
      >
        <slot name="attachment" />
      </div>
    </div>

    <div
      v-if="$slots.hasOwnProperty('button')"
      :class="$style.button"
      @click.stop
    >
      <slot name="button" />
    </div>
  </div>
</template>

<script>
import inputMixin from 'lib/mixins/inputMixin';

import BaseInput from 'atoms/base/BaseInput';
import BaseTextarea from 'atoms/base/BaseTextarea';
import HelpLink from 'molecules/HelpLink';

const AVAILABLE_KEY_CODES = [
  8, // Backspace
  9, // Tab
  13, // Enter
  16, // Shift
  17, // Ctrl
  18, // Alt/Option
  20, // Caps Lock
  27, // Escape
  37, // Arrow Left
  38, // Arrow Up
  39, // Arrow Right
  40, // Arrow Bottom
  46, // Delete
  91, // Left Command
  93, // Right Command
];

export default {
  name: 'LabeledInput',
  mixins: [
    inputMixin,
  ],
  components: {
    BaseInput,
    BaseTextarea,
    HelpLink,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    helpLink: String,
    useCounter: Boolean,
  },
  computed: {
    currentComponent() {
      return this.type === 'textarea' ? BaseTextarea : BaseInput;
    },
    counter() {
      return this.maxlength - this.value.length;
    },
  },
  methods: {
    handleKeydown(event) {
      if (!this.useCounter) {
        return;
      }

      if (this.value.length < this.maxlength) {
        return;
      }

      if (!AVAILABLE_KEY_CODES.includes(event.keyCode)) {
        this.$alert({
          type: 'warning',
          text: 'too.long.text',
        });

        event.preventDefault();
        return;
      }
    },
  },
};
</script>

<style lang="sass" module>
  @import '~styles/components/base/input'
</style>
